import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tags, FileText, ScrollText, BarChart3, Mail, Settings, Shield, Bell, Database, Merge, History } from 'lucide-react';

const settingsItems = [
  {
    icon: Tags,
    title: 'Company Categories',
    description: 'Manage categories for organizing companies',
    path: '/company-categories',
    color: 'bg-blue-500'
  },
  {
    icon: FileText,
    title: 'Print Templates',
    description: 'Configure certificate print layouts and field positions',
    path: '/certificate-templates',
    color: 'bg-emerald-500'
  },
  {
    icon: ScrollText,
    title: 'Declarations',
    description: 'Manage declaration texts for certificates',
    path: '/declarations',
    color: 'bg-purple-500'
  },
  {
    icon: BarChart3,
    title: 'Reports',
    description: 'Generate and export certificate reports',
    path: '/reports',
    color: 'bg-orange-500'
  },
  {
    icon: Mail,
    title: 'Email Marketing',
    description: 'Send promotional emails to students who opted in',
    path: '/email-marketing',
    color: 'bg-pink-500'
  },
  {
    icon: Bell,
    title: 'Expiry Reminders',
    description: 'Send certificate expiry notifications to companies',
    path: '/expiry-reminders',
    color: 'bg-amber-500'
  },
  {
    icon: Settings,
    title: 'Email Settings',
    description: 'Configure Resend API settings for system emails',
    path: '/email-settings',
    color: 'bg-slate-600'
  },
  {
    icon: Shield,
    title: 'Audit Logs',
    description: 'Track system activities and user actions',
    path: '/audit-logs',
    color: 'bg-indigo-500'
  },
  {
    icon: Database,
    title: 'Backup & Restore',
    description: 'Download a full offline backup or restore from one',
    path: '/backup',
    color: 'bg-rose-600'
  },
  {
    icon: Merge,
    title: 'Merge Duplicates',
    description: 'Find and merge duplicate companies and students',
    path: '/merge-duplicates',
    color: 'bg-cyan-600'
  },
  {
    icon: History,
    title: 'Merge History',
    description: 'View past merges (last 30 days) and undo them if needed',
    path: '/merge-history',
    color: 'bg-amber-600'
  }
];

const AdminSettings = () => {
  usePageTitle('Settings');
  const navigate = useNavigate();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-500 mt-1">
          Configure system settings and manage templates
        </p>
      </div>

      {/* Settings Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsItems.map((item) => {
          const Icon = item.icon;
          return (
            <Card 
              key={item.path}
              className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] border-slate-200"
              onClick={() => navigate(item.path)}
              data-testid={`settings-${item.path.replace('/', '')}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {item.title}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-slate-600">
                  {item.description}
                </CardDescription>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminSettings;
